import moment from 'moment';
import { ICalendarEvent } from 'model/calendar/events';
import {
  IProviderCaseloadClient,
  IProviderTemplateSummary
} from 'model/calendar/events';
import { NON_BOOKABLE_AVAILABILITY_TYPES } from 'utils/constants/availabilityTemplate';
import {
  formatSeriesStartDate,
  isSeriesStartingLater
} from 'utils/validators/planner';

/**
 * Billable-hours totals and the client roster for the provider sidebar, derived
 * from the slots the recurring template actually draws — nothing else.
 *
 * Available hours are therefore checkable by reading the calendar: they are the
 * billable slots on screen, with lunch / meetings / notes excluded. That matters
 * because schedulers will validate these numbers by hand.
 *
 * Booked hours are NOT computed here. They come from the backend
 * (providerCapacity.providerBookedHours), recomputed on event create / cancel /
 * edit alongside providerBookedSessions, so the booked figure has one owner and
 * the sessions and hours bars can never disagree about which events they counted.
 */

const MINUTES_PER_HOUR = 60;

/** Weekday order used by the breakdown, matching the calendar left to right. */
export const BREAKDOWN_DAYS = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun'
] as const;

export type BreakdownDay = typeof BREAKDOWN_DAYS[number];

const DAY_INDEX: Record<number, string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat'
};

const roundToQuarter = (hours: number) => Math.round(hours * 4) / 4;

const durationMinutes = (slot: ICalendarEvent): number => {
  if (!slot.start || !slot.end) return 0;
  const mins =
    (new Date(slot.end).getTime() - new Date(slot.start).getTime()) / 60000;
  return mins > 0 ? mins : 0;
};

const isNonBookableSlot = (slot: ICalendarEvent): boolean =>
  NON_BOOKABLE_AVAILABILITY_TYPES.includes(
    (slot.availType || '').toLowerCase()
  );

/** Sort key placing Monday first and earlier times before later ones. */
const slotOrder = (weekday: string, date: Date) =>
  BREAKDOWN_DAYS.indexOf(weekday as BreakdownDay) * 24 * 60 +
  date.getHours() * 60 +
  date.getMinutes();

export const buildTemplateSummary = (
  templateSlots: ICalendarEvent[],
  providerId?: number
): IProviderTemplateSummary => {
  const clientIds = new Set<number>();
  // Keyed by client so repeat sessions gather under one row.
  const caseload = new Map<
    number,
    IProviderCaseloadClient & { earliest: number }
  >();
  let excludedMinutes = 0;
  /** Bookable minutes per weekday; lunch / meetings / notes never land here. */
  const availableMinutesPerDay: Record<string, number> = {};

  templateSlots.forEach(slot => {
    if (slot.type === 'na' || !slot.start || !slot.end) return;

    if (slot.type === 'availability') {
      const mins = durationMinutes(slot);
      if (isNonBookableSlot(slot)) {
        excludedMinutes += mins;
        return;
      }
      const key = DAY_INDEX[new Date(slot.start).getDay()];
      availableMinutesPerDay[key] = (availableMinutesPerDay[key] ?? 0) + mins;
      return;
    }

    if (slot.isAdmin) return;

    const client = (slot as {
      client?: { id?: number; name?: string; profile?: { dob?: string } };
    }).client;
    const clientId = client?.id;
    if (clientId === undefined) return;
    clientIds.add(Number(clientId));

    // The roster mirrors the calendar, so cancelled instances of a live series
    // and not-yet-started series both appear — the latter marked with when they
    // begin, so the list reconciles with what is on the grid.
    const start = new Date(slot.start!);
    const weekday = DAY_INDEX[start.getDay()];
    const clinicTimezone = slot.clinic?.timezone;
    const isUpcoming = isSeriesStartingLater(
      slot.seriesStartDate,
      clinicTimezone
    );
    const session = {
      weekday,
      time: moment(start).format('h:mm A'),
      isUpcoming,
      ...(isUpcoming
        ? {
            startsOn: formatSeriesStartDate(slot.seriesStartDate, clinicTimezone)
          }
        : {})
    };

    const order = slotOrder(weekday, start);
    const existing = caseload.get(Number(clientId));
    if (existing) {
      existing.sessions.push(session);
      existing.earliest = Math.min(existing.earliest, order);
      return;
    }
    caseload.set(Number(clientId), {
      clientId: Number(clientId),
      name: client?.name || '',
      dob: client?.profile?.dob,
      sessions: [session],
      earliest: order
    });
  });

  // Each day rounded to the quarter hour, and the headline taken as the sum of
  // those rounded rows rather than a separately rounded raw total. Rounding both
  // independently lets them disagree, and the whole point of the breakdown is
  // checking the figure by hand. Quarters are exact in binary floating point, so
  // summing them introduces no drift.
  const perDay = BREAKDOWN_DAYS.filter(
    day => availableMinutesPerDay[day] !== undefined
  ).map(day => ({
    day,
    availableHours: roundToQuarter(
      availableMinutesPerDay[day] / MINUTES_PER_HOUR
    )
  }));

  // Earliest slot in the week first; within a client, day then time.
  const sortedCaseload = Array.from(caseload.values())
    .sort((a, b) => a.earliest - b.earliest || a.name.localeCompare(b.name))
    .map(({ earliest, ...client }) => ({
      ...client,
      sessions: [...client.sessions].sort(
        (a, b) =>
          BREAKDOWN_DAYS.indexOf(a.weekday as BreakdownDay) -
            BREAKDOWN_DAYS.indexOf(b.weekday as BreakdownDay) ||
          moment(a.time, 'h:mm A').diff(moment(b.time, 'h:mm A'))
      )
    }));

  return {
    providerId,
    availableHours: perDay.reduce((total, day) => total + day.availableHours, 0),
    excludedHours: roundToQuarter(excludedMinutes / MINUTES_PER_HOUR),
    uniqueClients: clientIds.size,
    perDay,
    caseload: sortedCaseload
  };
};
